import { Box, Divider, Drawer, Tab, Tabs, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { controlKeys, remediationKeys } from "../../../api/queryKeys";
import StatusChip from "../../../components/chips/StatusChip";
import LabelComponentProp from "../../../components/LabelComponentProp";
import LoadingIcon from "../../../components/LoadingIcon";
import SvgToIcon from "../../../icons/SvgToIcon";
import { DRAWER_TABS } from "../../../utils/constants";
import ControlRemediationDrawer from "../../posture/controls/components/ControlRemediationDrawer/ControlRemediationDrawer";
import TabbedMitigationControlsTable from "../../posture/steps/components/TabbedMitigationControlsTable";
import { operationalHeatmap } from "../../stack/environment/icons/iconExport";
import {
  getCoverageColor,
  getCoverageLabel,
  getRiskColor,
  getRiskLabel,
} from "./utils";

const HeatmapPracticeDrawer = ({
  isOpen,
  onClose,
  practice,
  entityType,
  domain,
  showRisk,
}) => {
  const colors = useColors();
  const { fetchControlsBatch, trackEvent, fetchAllRemediations } = useAPI();

  const [activeTab, setActiveTab] = useState(1);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);

  const resetSelection = () => {
    setOpenDrawer(false);
    setSelectedControl(null);
    trackEvent("User closed the practice drawer");
  };

  const onRowClick = (control_id) => {
    setOpenDrawer(true);
    setSelectedControl(
      controls.find((control) => control.control_id === control_id)
    );

    trackEvent(
      "User clicked on a control " +
        control_id +
        " in the practice drawer: " +
        practice?.name
    );
  };

  const { data: controls, isLoading: controlsLoading } = useQuery({
    queryKey: controlKeys.batchControls(practice?.controls),
    queryFn: async () => {
      const controls = await fetchControlsBatch(practice?.controls);
      return controls;
    },
    select: (response) => response.data,
    enabled: practice?.controls?.length > 0,
  });

  const { data: allRemediations } = useQuery({
    queryKey: remediationKeys.all,
    queryFn: fetchAllRemediations,
    select: (response) =>
      response.data.map((remediation) => ({
        id: remediation.remediation_id,
        ...remediation,
      })),
  });
  const controlWithFeasibility = useMemo(
    () =>
      controls?.map((control) => {
        const remediation = allRemediations?.find(
          (remediation) => remediation.control_id === control.control_id
        );
        return {
          ...control,
          feasibility: remediation?.feasibility,
          improvement: practice?.improvements?.find(
            (improvement) => improvement.control_id === control.control_id
          )?.improvement,
        };
      }),
    [controls, allRemediations]
  );
  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        style: {
          width: "950px",
          backgroundColor: colors.grey_50,
        },
      }}
    >
      {/* Tab navigation */}
      <Tabs
        value={activeTab}
        onChange={(event, newValue) => {
          setActiveTab(newValue);
        }}
        textColor="inherit"
        sx={{
          backgroundColor: colors.primary_6percent,

          "& .MuiTab-root": {
            minWidth: 120,
            textTransform: "none",
            fontWeight: "bold",
            padding: "10px 15px",
          },
          "& .Mui-selected": {
            borderRadius: "8px 8px 0 0",
            boxShadow: "0px -1px 3px 0.5px  " + colors.primary_12percent,
            backgroundColor: colors.grey_50,
          },
        }}
        TabIndicatorProps={{
          style: {
            display: "none",
            backgroundColor: colors.primary_main,
          },
        }}
      >
        <Tab
          value={1}
          label={<Typography variant={"subtitle2"}>Overview</Typography>}
          sx={{
            textTransform: "none",
            paddingBottom: "0px",
            marginBottom: "-4px",
          }}
        />
      </Tabs>

      {/* Main content container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: 3,
          gap: 2,
        }}
      >
        {/* Practice title */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.text_secondary,
            }}
          >
            Security Practice
          </Typography>
          <Typography variant="h5">{practice?.name}</Typography>
        </Box>

        {/* Practice metadata and classification */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
          }}
        >
          {/* Infrastructure Pillar */}
          <LabelComponentProp
            label={"Infrastructure Pillar"}
            textTypography="caption"
            sx={{
              display: "flex",
              alignItems: "left",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text_primary,
                }}
              >
                {entityType}
              </Typography>
            </Box>
          </LabelComponentProp>

          <Divider orientation="vertical" flexItem />

          {/* Security Domain */}
          <LabelComponentProp
            label={"Security Domain"}
            textTypography="caption"
            sx={{
              display: "flex",
              alignItems: "left",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.text_primary,
              }}
            >
              {domain}
            </Typography>
          </LabelComponentProp>

          <Divider orientation="vertical" flexItem />

          {/* Subpillar with icon */}
          <LabelComponentProp textTypography={"caption"} label={"Sub Pillar"}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <SvgToIcon
                IconLogo={operationalHeatmap[practice?.subPillar]}
                style={{
                  width: "24px",
                  height: "24px",
                }}
              />
              <Typography variant="caption">{practice?.subPillar}</Typography>
            </Box>
          </LabelComponentProp>
          {practice?.severity > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <LabelComponentProp
                label={showRisk ? "Risk" : "Coverage"}
                textTypography="caption"
                sx={{
                  display: "flex",
                  alignItems: "left",
                }}
              >
                <StatusChip
                  circleColor={
                    showRisk
                      ? getRiskColor(practice?.severity, colors)
                      : getCoverageColor(practice?.severity, colors)
                  }
                  label={
                    showRisk
                      ? getRiskLabel(practice?.severity)
                      : getCoverageLabel(practice?.severity)
                  }
                  insetBoxShadow={true}
                />
              </LabelComponentProp>
            </>
          )}
        </Box>

        <Divider />

        {/* Associated controls section */}
        <Box
          sx={{
            marginTop: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="subtitle1">
            What controls are associated with this security practice?
          </Typography>

          {/* Controls display - shows either a table of controls, loading indicator, or "no controls" message */}
          {!controlsLoading ? (
            controls?.length > 0 ? (
              <>
                {/* Table of associated controls */}
                <TabbedMitigationControlsTable
                  mitigatingControls={controlWithFeasibility}
                  loading={controlsLoading}
                  onRowClick={onRowClick}
                  boxSXProps={{
                    border: "0px",
                  }}
                  filterColumns={[
                    "is_detected",
                    "control_id",
                    "name_for_gap_state",
                    "name_for_correct_state",
                    "tool",
                    "remediation_priority",
                    "affected_entity_counts_by_type",
                    "severity",
                    "feasibility",
                    "improvement",
                  ]}
                />

                {/* Drawer to show selected control details */}
                <ControlRemediationDrawer
                  open={openDrawer}
                  onClose={resetSelection}
                  selectedTab={DRAWER_TABS.CONTROL_TAB}
                  selectedControl={selectedControl}
                />
              </>
            ) : (
              <Typography variant="subtitle2">
                No mitigating controls found
              </Typography>
            )
          ) : (
            <LoadingIcon size={25} />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default HeatmapPracticeDrawer;
